import Dexie from 'dexie';

// FT-324 (2026-07-06): Pre-compiled wormhole regex — MUST match storage.js FT324_CHECKLIST_WORMHOLE_RE.
// Kept in sync manually because storage.js can't be imported here (extractModule is duplicated by
// design — see comment on extractModule below).
const FT324_UUID_RE = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
const FT324_LEGACY_VESSEL_RE = '[A-Z]{2,4}[-\\d]+[-\\d]*';
const FT324_CHECKLIST_WORMHOLE_RE = new RegExp(
  `^checklist_(?:${FT324_UUID_RE}|${FT324_LEGACY_VESSEL_RE})(?:_(?:${FT324_UUID_RE}|${FT324_LEGACY_VESSEL_RE}))?$`,
  'i'
);

// FleetSkipper IndexedDB Database (Dexie name 'SeaReadyDeck' retained — protected per BRANDING §1.2)
export const db = new Dexie('SeaReadyDeck');

db.version(1).stores({
  // Primary storage table (mimics localStorage key-value pairs)
  storage: 'key, vesselId, module, timestamp',

  // Sync queue (for offline changes waiting to push)
  syncQueue: '++id, vesselId, module, operation, timestamp, synced',

  // Audit log (for debugging and compliance)
  auditLog: '++id, key, action, timestamp, userId'
});

// FT-341 (2026-07-08): v2 adds photos + photoThumbs tables for evidence Blobs.
// Photos live outside `db.storage` because they are Blobs (not JSON) and must
// NOT trigger `sr:storage:set` events (see DESIGN-BRIEF §2.1). Compound index
// `[vesselId+recordId]` powers vessel-scoped per-record queries.
db.version(2).stores({
  storage: 'key, vesselId, module, timestamp',
  syncQueue: '++id, vesselId, module, operation, timestamp, synced',
  auditLog: '++id, key, action, timestamp, userId',
  photos: 'photoId, vesselId, recordType, recordId, capturedAt, [vesselId+recordId]',
  photoThumbs: 'photoId, vesselId'
});

// Helper: Extract module name from key
// MUST match storage.js extractModule exactly to prevent sync routing mismatches
export const extractModule = (key) => {
  // Org-scoped modules (no vessel ID suffix)
  if (key === 'fleet_crew' || key.startsWith('fleet_crew_')) return 'fleet_crew';
  if (key === 'fleet_risk_assessments' || key.startsWith('fleet_risk_assessments_')) return 'fleet_risk_assessments';

  // Crew modules - specific first
  if (key.startsWith('crew_familiarisations_')) return 'crew_familiarisations';
  if (key.startsWith('crew_')) return 'crew';

  // Equipment
  if (key.startsWith('equipment_register_')) return 'equipment_register';
  if (key.startsWith('equipment_selections_')) return 'equipment_selections';

  // Maintenance - specific patterns
  if (key.startsWith('maintenance_schedule_')) return 'maintenance_schedule';
  if (key.startsWith('maintenance_jobs_')) return 'maintenance_jobs';
  if (key.startsWith('maintenance_history_')) return 'maintenance_history';
  if (key.startsWith('maintenance_config_')) return 'maintenance_config';
  if (key.startsWith('job_descriptions_')) return 'job_descriptions';

  // Drills - config MUST come before drill_
  if (key.startsWith('drills_config_')) return 'drills_config';
  if (key.startsWith('drill_settings_')) return 'drill_settings';
  if (key.startsWith('drill_register_')) return 'drill_register';  // FT-406 FIX: MUST come before drill_ - else collides with 'drills'/sr.drills.v1
  if (key.startsWith('drill_')) return 'drills';

  // Defects - config MUST come before defects_
  if (key.startsWith('defects_config_')) return 'defects_config';
  if (key.startsWith('defects_')) return 'defects';

  // Incidents - config MUST come before incidents_
  if (key.startsWith('incidents_config_')) return 'incidents_config';
  if (key.startsWith('incidents_')) return 'incidents';

  // Risk assessments - config MUST come before risk_
  if (key.startsWith('risk_assessment_config_')) return 'risk_assessment_config';
  if (key.startsWith('risk_')) return 'risk_assessments';

  // Checklists
  if (key.startsWith('checklist_configs_')) return 'checklist_configs';

  // Voyages - settings MUST come before voyage_
  if (key.startsWith('voyage_settings_')) return 'voyage_settings';
  if (key.startsWith('voyage_phases_')) return 'voyage_phases';  // FT-461 FIX: MUST come before voyage_ - else collides with 'voyages'/sr.voyages.v1
  if (key.startsWith('voyage_')) return 'voyages';

  // Daily checks
  if (key.startsWith('daily_checks_')) return 'daily_checks';

  // Hours of rest - handle org-scoped key
  if (key === 'hours_of_rest' || key.startsWith('hours_of_rest_')) return 'hours_of_rest';

  // Vessel settings
  if (key.startsWith('vesselSettings_')) return 'vesselSettings';

  // Operations - SPECIFIC patterns MUST come before any generic pattern
  if (key.startsWith('operations_daily_checks_config_')) return 'operations_daily_checks_config';
  if (key.startsWith('operations_main_logbook_')) return 'operations_main_logbook';
  if (key.startsWith('operations_voyages_')) return 'operations_voyages';
  if (key.startsWith('operations_checklist_templates_')) return 'operations_checklist_templates';
  if (key.startsWith('operations_checklist_favourites_')) return 'operations_checklist_favourites';
  if (key.startsWith('operations_checklists_')) return 'operations_checklists';
  if (key.startsWith('operations_permits_')) return 'operations_permits';
  if (key.startsWith('operations_bunkering_')) return 'operations_bunkering';
  if (key.startsWith('operations_voyage_templates_')) return 'operations_voyage_templates';

  // Special operations
  if (key.startsWith('pilot_transfers_')) return 'pilot_transfers';
  if (key.startsWith('towing_operations_')) return 'towing_operations';
  if (key.startsWith('diving_operations_')) return 'diving_operations';

  // Checklist records
  // FT-324 (2026-07-06): 'seaready_checklist_records_*' kept as migration compatibility path;
  // canonical key is now 'checklist_records_*'. See storage.js for the same rule + rationale.
  if (key.startsWith('seaready_checklist_records_')) return 'checklist_records';
  if (key.startsWith('checklist_records_')) return 'checklist_records';
  // FT-324 (2026-07-06): Kill ghost-doc wormhole — see storage.js extractModule for full detail.
  // Must come BEFORE the generic underscore fallback so `checklist_${vesselId}` (and its
  // org-scoped form `checklist_${orgId}_${vesselId}`) resolves to checklist_records instead of
  // the bare 'checklist' module name.
  if (FT324_CHECKLIST_WORMHOLE_RE.test(key)) return 'checklist_records';

  // Familiarisation - SPECIFIC patterns MUST come before generic
  if (key.startsWith('familiarisation_checklist_templates_')) return 'familiarisation_checklist_templates';
  if (key.startsWith('familiarisationSettings_')) return 'familiarisationSettings';
  if (key.startsWith('familiarisation_records_')) return 'familiarisation_records';
  if (key.startsWith('familiarisation_progress_')) return 'familiarisation_progress';

  // Documents and certificates
  if (key.startsWith('documents_')) return 'documents';
  if (key.startsWith('certificates_')) return 'certificates';

  // Logbook and training
  if (key.startsWith('log_entries_')) return 'log_entries';
  if (key.startsWith('onboard_training_')) return 'onboard_training';

  // Audits and compliance
  if (key.startsWith('audits_')) return 'audits';
  if (key.startsWith('audit_log_')) return 'audit_log';
  if (key.startsWith('compliance_items_')) return 'compliance_items';
  if (key.startsWith('compliance_register_')) return 'compliance_register';

  // SMS documents
  if (key.startsWith('sms_documents_')) return 'sms_documents';

  const underscoreIndex = key.indexOf('_');
  return underscoreIndex > 0 ? key.substring(0, underscoreIndex) : key;
};

// Helper: Extract vessel ID from key
// FT-112 FIX: Added UUID pattern to match storage.js implementation
export const extractVesselId = (key) => {
  // Split by underscore and check segments right-to-left
  // This ensures we get the vessel ID, not the org ID in org-scoped keys
  const segments = key.split('_');

  // Check segments from right to left
  for (let i = segments.length - 1; i >= 0; i--) {
    const segment = segments[i];

    // Match UUID pattern: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment)) {
      return segment;
    }

    // Match legacy vessel ID pattern: VSL001-25, VSL-001, etc.
    if (/^[A-Z]{2,4}[-\d]+[-\d]*$/i.test(segment)) {
      return segment;
    }
  }

  // Only log failures to reduce console noise
  console.warn(`[DB] extractVesselId: ${key} → default (no vessel ID found)`);
  return 'default';
};

// Helper: Get all keys for a specific vessel (enables selective sync)
export const getKeysForVessel = async (vesselId) => {
  return db.storage.where('vesselId').equals(vesselId).toArray();
};

// Helper: Get all unique vessel IDs (for multi-vessel support)
export const getAllVesselIds = async () => {
  const records = await db.storage.toArray();
  const vesselIds = new Set(records.map(r => r.vesselId));
  return Array.from(vesselIds).filter(id => id !== 'default');
};
